import React, { useState, useContext, useEffect } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import { 
  FileText, Package, Truck, CheckCircle, AlertCircle, Clock, 
  TrendingUp, TrendingDown, RefreshCw, Search, Filter, Eye, X,
  ChevronDown, ChevronUp, AlertTriangle, BarChart3
} from 'lucide-react';

function POvsDelivered() {
  const { API } = useContext(AppContext);
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [expandedRow, setExpandedRow] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/stores/po-vs-delivered`);
      setData(response.data.data || []);
      setSummary(response.data.summary || {});
    } catch (error) {
      console.error('Error loading PO vs Delivered data:', error);
    }
    setLoading(false);
  };

  // Filter data
  const filteredData = data.filter(item => {
    const matchesSearch = 
      item.poId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.supplier?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterStatus === 'All' || item.deliveryStatus === filterStatus;
    
    return matchesSearch && matchesFilter;
  });

  // Get status styling
  const getDeliveryStatusStyle = (status) => {
    switch (status) {
      case 'Fully Delivered':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'Partially Delivered':
        return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'Pending':
        return 'bg-gray-100 text-gray-600 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  const getDeliveryStatusIcon = (status) => {
    switch (status) {
      case 'Fully Delivered':
        return <CheckCircle size={14} className="text-green-600" />;
      case 'Partially Delivered':
        return <Clock size={14} className="text-amber-600" />;
      case 'Pending':
        return <Package size={14} className="text-gray-500" />;
      default:
        return <Package size={14} className="text-gray-500" />;
    }
  };

  // Get variance styling
  const getVarianceStyle = (variance) => {
    if (variance > 0) return 'text-green-600';
    if (variance < 0) return 'text-red-600';
    return 'text-gray-600';
  };

  // Calculate delivery percentage
  const getDeliveryPercent = (ordered, delivered) => {
    if (ordered === 0) return 0;
    return Math.min(100, (delivered / ordered) * 100);
  };

  return (
    <div className="space-y-4" data-testid="po-vs-delivered-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-lg">
            <BarChart3 className="text-white" size={24} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">PO vs Delivered</h1>
            <p className="text-sm text-gray-500">Compare Purchase Orders with GRN deliveries</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {/* Search */}
          <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
            <Search size={16} className="text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search PO, Supplier..."
              className="bg-transparent border-none outline-none text-sm w-44"
              data-testid="search-input"
            />
          </div>

          {/* Filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:border-indigo-400"
            data-testid="status-filter"
          >
            <option value="All">All Status</option>
            <option value="Fully Delivered">Fully Delivered</option>
            <option value="Partially Delivered">Partially Delivered</option>
            <option value="Pending">Pending</option>
          </select>

          {/* Refresh */}
          <button
            onClick={loadData}
            className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
            title="Refresh"
          >
            <RefreshCw size={16} className="text-gray-600" />
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white">
          <div className="flex items-center gap-2 mb-1">
            <FileText size={16} className="opacity-80" />
            <p className="text-xs opacity-80">Total POs</p>
          </div>
          <p className="text-2xl font-bold">{summary.totalPOs || 0}</p>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle size={16} className="opacity-80" />
            <p className="text-xs opacity-80">Fully Delivered</p>
          </div>
          <p className="text-2xl font-bold">{summary.fullyDelivered || 0}</p>
        </div>
        <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-4 text-white">
          <div className="flex items-center gap-2 mb-1">
            <Clock size={16} className="opacity-80" />
            <p className="text-xs opacity-80">Partial</p>
          </div>
          <p className="text-2xl font-bold">{summary.partiallyDelivered || 0}</p>
        </div>
        <div className="bg-gradient-to-br from-gray-500 to-gray-600 rounded-xl p-4 text-white">
          <div className="flex items-center gap-2 mb-1">
            <Package size={16} className="opacity-80" />
            <p className="text-xs opacity-80">Pending</p>
          </div>
          <p className="text-2xl font-bold">{summary.pending || 0}</p>
        </div>
        <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-4 text-white">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle size={16} className="opacity-80" />
            <p className="text-xs opacity-80">With Shortage</p>
          </div>
          <p className="text-2xl font-bold">{summary.withShortage || 0}</p>
        </div>
      </div>

      {/* Value Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500 mb-1">Total Ordered Value</p>
              <p className="text-2xl font-bold text-gray-900">₹{(summary.totalOrderedValue || 0).toLocaleString()}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <FileText size={24} className="text-blue-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500 mb-1">Total Delivered Value (Est.)</p>
              <p className="text-2xl font-bold text-green-600">₹{(summary.totalDeliveredValue || 0).toLocaleString()}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <Truck size={24} className="text-green-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Comparison Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="p-3 border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-purple-50">
          <h3 className="font-bold text-gray-900 text-sm flex items-center gap-2">
            <BarChart3 size={16} className="text-indigo-500" />
            Purchase Order vs Delivery Comparison
          </h3>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-3 py-2.5 text-left font-semibold text-gray-700">PO Number</th>
                <th className="px-3 py-2.5 text-left font-semibold text-gray-700">Supplier</th>
                <th className="px-3 py-2.5 text-left font-semibold text-gray-700">PO Date</th>
                <th className="px-3 py-2.5 text-left font-semibold text-gray-700">Due Date</th>
                <th className="px-3 py-2.5 text-right font-semibold text-gray-700">Ordered Qty</th>
                <th className="px-3 py-2.5 text-right font-semibold text-gray-700">Delivered Qty</th>
                <th className="px-3 py-2.5 text-center font-semibold text-gray-700">Progress</th>
                <th className="px-3 py-2.5 text-right font-semibold text-gray-700">Variance</th>
                <th className="px-3 py-2.5 text-center font-semibold text-gray-700">GRNs</th>
                <th className="px-3 py-2.5 text-center font-semibold text-gray-700">Status</th>
                <th className="px-3 py-2.5 text-center font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={11} className="px-4 py-8 text-center text-gray-500">
                    <div className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-indigo-500 border-t-transparent"></div>
                      Loading...
                    </div>
                  </td>
                </tr>
              ) : filteredData.length === 0 ? (
                <tr>
                  <td colSpan={11} className="px-4 py-8 text-center text-gray-500">
                    No comparison data found
                  </td>
                </tr>
              ) : (
                filteredData.map((item) => {
                  const deliveryPercent = getDeliveryPercent(item.orderedQty, item.deliveredQty);
                  const isExpanded = expandedRow === item.poId;
                  
                  return (
                    <React.Fragment key={item.poId}>
                      <tr 
                        className={`hover:bg-indigo-50/50 transition cursor-pointer ${isExpanded ? 'bg-indigo-50' : ''}`}
                        onClick={() => setExpandedRow(isExpanded ? null : item.poId)}
                        data-testid={`po-row-${item.poId}`}
                      >
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-2">
                            {isExpanded ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
                            <span className="font-mono text-indigo-600 font-medium">{item.poId}</span>
                          </div>
                        </td>
                        <td className="px-3 py-3 font-medium text-gray-900">{item.supplier}</td>
                        <td className="px-3 py-3 text-gray-600">{item.poDate}</td>
                        <td className="px-3 py-3 text-gray-600">{item.deliveryDate}</td>
                        <td className="px-3 py-3 text-right font-semibold text-gray-900">
                          {item.orderedQty?.toLocaleString()}
                          <span className="ml-1 text-xs text-gray-500 font-normal">{item.orderedUom}</span>
                        </td>
                        <td className="px-3 py-3 text-right font-semibold text-blue-600">
                          {item.deliveredQty?.toLocaleString()}
                          <span className="ml-1 text-xs text-gray-500 font-normal">{item.deliveredUom || item.orderedUom}</span>
                        </td>
                        <td className="px-3 py-3">
                          <div className="w-full">
                            <div className="flex items-center justify-between text-[10px] mb-1">
                              <span className="text-gray-500">{deliveryPercent.toFixed(0)}%</span>
                            </div>
                            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div 
                                className={`h-full rounded-full transition-all ${
                                  deliveryPercent >= 100 ? 'bg-green-500' :
                                  deliveryPercent > 0 ? 'bg-amber-500' : 'bg-gray-300'
                                }`}
                                style={{ width: `${Math.min(100, deliveryPercent)}%` }}
                              />
                            </div>
                          </div>
                        </td>
                        <td className={`px-3 py-3 text-right font-semibold ${getVarianceStyle(item.variance)}`}>
                          <div className="flex items-center justify-end gap-1">
                            {item.variance > 0 ? <TrendingUp size={12} /> : item.variance < 0 ? <TrendingDown size={12} /> : null}
                            {item.variance > 0 ? '+' : ''}{item.variance?.toLocaleString()}
                          </div>
                          <span className="text-[10px] font-normal">({item.variancePercent}%)</span>
                        </td>
                        <td className="px-3 py-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <span className="font-semibold">{item.grnCount}</span>
                            {item.shortageCount > 0 && (
                              <span className="px-1.5 py-0.5 bg-red-100 text-red-600 rounded text-[10px] font-medium">
                                {item.shortageCount} short
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-3 text-center">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${getDeliveryStatusStyle(item.deliveryStatus)}`}>
                            {getDeliveryStatusIcon(item.deliveryStatus)}
                            {item.deliveryStatus}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-center">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedPO(item);
                              setShowDetailModal(true);
                            }}
                            className="p-1.5 text-indigo-500 hover:bg-indigo-100 rounded-lg transition"
                            title="View Details"
                          >
                            <Eye size={14} />
                          </button>
                        </td>
                      </tr>
                      
                      {/* Expanded Row - GRN Details */}
                      {isExpanded && item.grns && item.grns.length > 0 && (
                        <tr className="bg-gray-50">
                          <td colSpan={11} className="px-6 py-3">
                            <div className="text-xs">
                              <p className="font-semibold text-gray-700 mb-2">GRN Deliveries for {item.poId}:</p>
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                                {item.grns.map((grn, idx) => (
                                  <div key={idx} className="bg-white rounded-lg p-2 border border-gray-200">
                                    <div className="flex items-center justify-between mb-1">
                                      <span className="font-mono text-amber-600 font-medium">{grn.id}</span>
                                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                        grn.result === 'Shortage' ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'
                                      }`}>
                                        {grn.result || 'Accepted'}
                                      </span>
                                    </div>
                                    <div className="text-gray-600">
                                      <p>Invoice: <span className="text-blue-600">{grn.invoiceNo}</span></p>
                                      <p>Qty: <span className="font-semibold">{(grn.quantity || grn.netWeight)?.toLocaleString()} kg</span></p>
                                      <p>Date: {grn.date || grn.receivedDate}</p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                      
                      {/* Expanded Row - No GRNs */}
                      {isExpanded && (!item.grns || item.grns.length === 0) && (
                        <tr className="bg-gray-50">
                          <td colSpan={11} className="px-6 py-4 text-center text-gray-500 text-sm">
                            No GRN deliveries recorded for this PO
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Legend */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-800">
        <h4 className="font-semibold mb-2">Understanding the Comparison:</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
          <div>
            <p className="flex items-center gap-2 mb-1">
              <span className="w-3 h-3 rounded-full bg-green-500"></span>
              <strong>Fully Delivered:</strong> Received qty ≥ Ordered qty
            </p>
            <p className="flex items-center gap-2 mb-1">
              <span className="w-3 h-3 rounded-full bg-amber-500"></span>
              <strong>Partially Delivered:</strong> Some quantity received
            </p>
            <p className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-gray-400"></span>
              <strong>Pending:</strong> No delivery yet
            </p>
          </div>
          <div>
            <p className="flex items-center gap-2 mb-1">
              <TrendingUp size={12} className="text-green-600" />
              <strong>Positive Variance:</strong> Received more than ordered
            </p>
            <p className="flex items-center gap-2 mb-1">
              <TrendingDown size={12} className="text-red-600" />
              <strong>Negative Variance:</strong> Received less than ordered
            </p>
            <p className="flex items-center gap-2">
              <AlertTriangle size={12} className="text-red-600" />
              <strong>Shortage:</strong> GRN marked with shortage result
            </p>
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedPO && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
              <div className="flex items-center gap-2">
                <BarChart3 className="text-indigo-500" size={20} />
                <h2 className="font-bold text-lg">PO Details - {selectedPO.poId}</h2>
              </div>
              <button
                onClick={() => setShowDetailModal(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* PO Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Supplier</p>
                  <p className="font-semibold">{selectedPO.supplier}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">PO Date</p>
                  <p className="font-semibold">{selectedPO.poDate}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Due Date</p>
                  <p className="font-semibold">{selectedPO.deliveryDate}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">PO Status</p>
                  <p className="font-semibold">{selectedPO.poStatus}</p>
                </div>
              </div>

              {/* Comparison Summary */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <p className="text-xs text-blue-600 mb-1">Ordered Qty</p>
                  <p className="text-2xl font-bold text-blue-700">{selectedPO.orderedQty?.toLocaleString()}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <p className="text-xs text-green-600 mb-1">Delivered Qty</p>
                  <p className="text-2xl font-bold text-green-700">{selectedPO.deliveredQty?.toLocaleString()}</p>
                </div>
                <div className={`rounded-lg p-4 text-center ${selectedPO.variance >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                  <p className={`text-xs mb-1 ${selectedPO.variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>Variance</p>
                  <p className={`text-2xl font-bold ${selectedPO.variance >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                    {selectedPO.variance > 0 ? '+' : ''}{selectedPO.variance?.toLocaleString()}
                  </p>
                  <p className="text-xs">({selectedPO.variancePercent}%)</p>
                </div>
              </div>

              {/* Progress Bar */}
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-600">Delivery Progress</span>
                  <span className="font-semibold">{getDeliveryPercent(selectedPO.orderedQty, selectedPO.deliveredQty).toFixed(1)}%</span>
                </div>
                <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full transition-all ${
                      getDeliveryPercent(selectedPO.orderedQty, selectedPO.deliveredQty) >= 100 ? 'bg-green-500' :
                      getDeliveryPercent(selectedPO.orderedQty, selectedPO.deliveredQty) > 0 ? 'bg-amber-500' : 'bg-gray-300'
                    }`}
                    style={{ width: `${Math.min(100, getDeliveryPercent(selectedPO.orderedQty, selectedPO.deliveredQty))}%` }}
                  />
                </div>
              </div>

              {/* PO Items */}
              {selectedPO.items && selectedPO.items.length > 0 && (
                <div className="border-t border-gray-200 pt-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">PO Line Items</h3>
                  <div className="bg-gray-50 rounded-lg overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-3 py-2 text-left">Material</th>
                          <th className="px-3 py-2 text-right">Quantity</th>
                          <th className="px-3 py-2 text-right">Rate</th>
                          <th className="px-3 py-2 text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedPO.items.map((item, idx) => (
                          <tr key={idx} className="border-t border-gray-200">
                            <td className="px-3 py-2 font-medium">{item.material || item.description}</td>
                            <td className="px-3 py-2 text-right">{item.quantity?.toLocaleString()}</td>
                            <td className="px-3 py-2 text-right">₹{item.rate?.toLocaleString()}</td>
                            <td className="px-3 py-2 text-right font-semibold">₹{item.amount?.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* GRN List */}
              <div className="border-t border-gray-200 pt-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">GRN Deliveries ({selectedPO.grnCount})</h3>
                {selectedPO.grns && selectedPO.grns.length > 0 ? (
                  <div className="space-y-2">
                    {selectedPO.grns.map((grn, idx) => (
                      <div key={idx} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-mono text-amber-600 font-medium">{grn.id}</span>
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              grn.result === 'Shortage' ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'
                            }`}>
                              {grn.result === 'Shortage' && <AlertTriangle size={10} className="inline mr-1" />}
                              {grn.result || 'Accepted'}
                            </span>
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              grn.status === 'Verified' ? 'bg-green-100 text-green-600' : 'bg-yellow-100 text-yellow-600'
                            }`}>
                              {grn.status}
                            </span>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-gray-600">
                          <div>
                            <span className="text-gray-500">Invoice:</span> <span className="text-blue-600 font-medium">{grn.invoiceNo}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Qty:</span> <span className="font-semibold">{(grn.quantity || grn.netWeight)?.toLocaleString()} kg</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Date:</span> {grn.date || grn.receivedDate}
                          </div>
                          <div>
                            <span className="text-gray-500">Material:</span> {grn.material || grn.commodityName}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-lg p-6 text-center text-gray-500">
                    No GRN deliveries recorded for this PO
                  </div>
                )}
              </div>
            </div>

            <div className="p-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowDetailModal(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition text-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default POvsDelivered;
